/**
 * Rutas de la aplicación
 * Usar estas constantes en lugar de strings hardcodeados
 */
export const APP_ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  UNAUTHORIZED: '/unauthorized',
  NOT_FOUND: '/404',

  // Features
  APPOINTMENTS: '/appointments',
  APPOINTMENTS_DETAIL: (id: string) => `/appointments/${id}`,

  ATTENTION: '/attention',
  ATTENTION_DETAIL: (id: string) => `/attention/${id}`,

  CALENDAR: '/calendar',

  AUDIT: '/audit',

  STATISTICS: '/statistics',

  // Masters
  MASTERS: '/masters',
  MASTERS_CITIES: '/masters/cities',
  MASTERS_WORKFLOWS: '/masters/workflows',
  MASTERS_QUESTIONS: '/masters/questions',
} as const;

/**
 * Endpoints de la API
 * Centralizar todos los endpoints aquí
 */
export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    PERMISSIONS: '/auth/permissions',
    TOKEN: '/auth/token',
  },

  // Appointments
  APPOINTMENTS: {
    LIST: '/appointments',
    GET: (id: string) => `/appointments/${id}`,
    CREATE: '/appointments',
    UPDATE: (id: string) => `/appointments/${id}`,
    DELETE: (id: string) => `/appointments/${id}`,
    UPDATE_STATUS: (id: string) => `/appointments/${id}/status`,
  },

  // Attention
  ATTENTION: {
    LIST: '/attention',
    GET: (id: string) => `/attention/${id}`,
    CREATE: '/attention',
    UPDATE: (id: string) => `/attention/${id}`,
    DELETE: (id: string) => `/attention/${id}`,
  },

  // Calendar
  CALENDAR: {
    BY_DOCTOR: '/calendar/doctor',
    BY_SERVICE: '/calendar/service',
  },

  // Audit
  AUDIT: {
    LIST: '/audit',
    GET: (id: string) => `/audit/${id}`,
  },

  // Statistics
  STATISTICS: {
    DASHBOARD: '/statistics/dashboard',
    APPOINTMENTS: '/statistics/appointments',
    DOCTORS: '/statistics/doctors',
  },

  // Masters
  MASTERS: {
    CITIES: {
      LIST: '/masters/cities',
      GET: (id: string) => `/masters/cities/${id}`,
      CREATE: '/masters/cities',
      UPDATE: (id: string) => `/masters/cities/${id}`,
      DELETE: (id: string) => `/masters/cities/${id}`,
    },
    WORKFLOWS: {
      LIST: '/masters/workflows',
      GET: (id: string) => `/masters/workflows/${id}`,
      CREATE: '/masters/workflows',
      UPDATE: (id: string) => `/masters/workflows/${id}`,
      DELETE: (id: string) => `/masters/workflows/${id}`,
    },
    QUESTIONS: {
      LIST: '/masters/questions',
      GET: (id: string) => `/masters/questions/${id}`,
      CREATE: '/masters/questions',
      UPDATE: (id: string) => `/masters/questions/${id}`,
      DELETE: (id: string) => `/masters/questions/${id}`,
    },
  },
} as const;

/**
 * Keys para localStorage
 */
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
  PERMISSIONS: 'permissions',
  THEME: 'theme',
} as const;

/**
 * Query keys para TanStack Query
 * Usar para mantener consistencia en el cache
 */
export const QUERY_KEYS = {
  AUTH: {
    ME: ['auth', 'me'] as const,
    PERMISSIONS: ['auth', 'permissions'] as const,
  },
  APPOINTMENTS: {
    ALL: ['appointments'] as const,
    LIST: (filters?: unknown) => ['appointments', 'list', filters] as const,
    DETAIL: (id: string) => ['appointments', 'detail', id] as const,
  },
  ATTENTION: {
    ALL: ['attention'] as const,
    LIST: (filters?: unknown) => ['attention', 'list', filters] as const,
    DETAIL: (id: string) => ['attention', 'detail', id] as const,
  },
  CALENDAR: {
    BY_DOCTOR: (doctorId?: string) => ['calendar', 'doctor', doctorId] as const,
    BY_SERVICE: (serviceId?: string) =>
      ['calendar', 'service', serviceId] as const,
  },
  AUDIT: {
    ALL: ['audit'] as const,
    LIST: (filters?: unknown) => ['audit', 'list', filters] as const,
    DETAIL: (id: string) => ['audit', 'detail', id] as const,
  },
  STATISTICS: {
    DASHBOARD: ['statistics', 'dashboard'] as const,
    APPOINTMENTS: (filters?: unknown) =>
      ['statistics', 'appointments', filters] as const,
    DOCTORS: (filters?: unknown) => ['statistics', 'doctors', filters] as const,
  },
  MASTERS: {
    CITIES: {
      ALL: ['masters', 'cities'] as const,
      LIST: (filters?: unknown) =>
        ['masters', 'cities', 'list', filters] as const,
      DETAIL: (id: string) => ['masters', 'cities', 'detail', id] as const,
    },
    WORKFLOWS: {
      ALL: ['masters', 'workflows'] as const,
      LIST: (filters?: unknown) =>
        ['masters', 'workflows', 'list', filters] as const,
      DETAIL: (id: string) => ['masters', 'workflows', 'detail', id] as const,
    },
    QUESTIONS: {
      ALL: ['masters', 'questions'] as const,
      LIST: (filters?: unknown) =>
        ['masters', 'questions', 'list', filters] as const,
      DETAIL: (id: string) => ['masters', 'questions', 'detail', id] as const,
    },
  },
} as const;

/**
 * HTTP Status Codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

/**
 * Permisos de la aplicación
 * Usar con CASL
 */
export const PERMISSIONS = {
  APPOINTMENTS: {
    MODULE: 'Appointments',
    ACCESS: 'access',
    READ: 'read',
    WRITE: 'write',
    DELETE: 'delete',
    // Acciones específicas
    UPDATE_STATUS: 'updateStatus',
    ASSIGN_DOCTOR: 'assignDoctor',
  },
  ATTENTION: {
    MODULE: 'Attention',
    ACCESS: 'access',
    READ: 'read',
    WRITE: 'write',
    DELETE: 'delete',
    // Campos específicos (ejemplo para 50+ campos)
    FIELDS: {
      DIAGNOSIS: 'diagnosis',
      TREATMENT: 'treatment',
      MEDICATIONS: 'medications',
      VITAL_SIGNS: 'vitalSigns',
      INTERNAL_NOTES: 'internalNotes',
      PATIENT_HISTORY: 'patientHistory',
      LAB_RESULTS: 'labResults',
      PRESCRIPTIONS: 'prescriptions',
    },
  },
  CALENDAR: {
    MODULE: 'Calendar',
    ACCESS: 'access',
    READ: 'read',
    WRITE: 'write',
    DELETE: 'delete',
    // Acciones específicas
    CREATE_APPOINTMENT: 'createAppointment',
    CANCEL_APPOINTMENT: 'cancelAppointment',
    RESCHEDULE: 'reschedule',
  },
  AUDIT: {
    MODULE: 'Audit',
    ACCESS: 'access',
    READ: 'read',
    // Auditoría típicamente solo lectura
    EXPORT: 'export',
  },
  STATISTICS: {
    MODULE: 'Statistics',
    ACCESS: 'access',
    READ: 'read',
    EXPORT: 'export',
  },
  MASTERS: {
    MODULE: 'Masters',
    ACCESS: 'access',
    READ: 'read',
    WRITE: 'write',
    DELETE: 'delete',
  },
} as const;

/**
 * Configuración de paginación
 */
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PER_PAGE: 20,
  PER_PAGE_OPTIONS: [10, 20, 50, 100] as const,
} as const;

/**
 * Configuración de tiempos (en milisegundos)
 */
export const TIMEOUTS = {
  API_REQUEST: 30000, // 30 segundos
  DEBOUNCE: 300, // 300ms
  TOAST: 5000, // 5 segundos
  SEARCH_DEBOUNCE: 500, // 500ms para búsquedas
} as const;

/**
 * Roles de usuario
 */
export const USER_ROLES = {
  ADMIN: 'admin',
  OPERATOR: 'operator', // Call center
  CORPORATE: 'corporate', // Configuración
  AUDITOR: 'auditor', // Solo lectura auditoría
  DOCTOR: 'doctor',
} as const;

/**
 * Estados de citas
 */
export const APPOINTMENT_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  NO_SHOW: 'no_show',
} as const;

/**
 * Colores de semaforización
 */
export const SEMAPHORE_COLORS = {
  GREEN: 'green',
  YELLOW: 'yellow',
  RED: 'red',
} as const;
